import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSync, faServer, faFolder, faClock, faCode, faTicketAlt, faLayerGroup, faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';
import { agentsApi } from '../../services/agentsApi';
import MarkdownRenderer from '../common/MarkdownRenderer';

interface Props {
  validationId: string;
}

/** Parse the structured text from get_workspace_context into sections */
function parseContextSections(raw: string): Record<string, string> {
  const sections: Record<string, string> = {};
  const lines = raw.split('\n');
  let currentKey = 'header';
  let currentLines: string[] = [];

  for (const line of lines) {
    // Detect section headers (all caps followed by colon or known patterns)
    const sectionMatch = line.match(/^(WORKSPACE CONTEXT|DESCRIPTION|UNDERSTANDING|TECH STACK|PACKAGE MANAGER|FILES|TOP-LEVEL|KEY FILES|CONTAINER|APP URL|RECENT AGENT WORK|RECENT ACTIVITY|TICKETS|APP STATUS|SERVICES)[:.]?\s*(.*)/);
    if (sectionMatch) {
      // Save previous section
      if (currentLines.length > 0) {
        sections[currentKey] = currentLines.join('\n').trim();
      }
      currentKey = sectionMatch[1].toLowerCase().replace(/\s+/g, '_');
      currentLines = sectionMatch[2] ? [sectionMatch[2]] : [];
    } else {
      currentLines.push(line);
    }
  }
  // Save last section
  if (currentLines.length > 0) {
    sections[currentKey] = currentLines.join('\n').trim();
  }

  return sections;
}

const SECTION_CONFIG: Record<string, { icon: any; label: string; color: string }> = {
  description: { icon: faFolder, label: 'Description', color: 'text-indigo-500' },
  understanding: { icon: faLayerGroup, label: 'Understanding', color: 'text-purple-500' },
  tech_stack: { icon: faCode, label: 'Tech Stack', color: 'text-blue-500' },
  container: { icon: faServer, label: 'Container', color: 'text-green-500' },
  recent_agent_work: { icon: faClock, label: 'Recent Agent Work', color: 'text-amber-500' },
  recent_activity: { icon: faClock, label: 'Recent Activity', color: 'text-orange-500' },
  tickets: { icon: faTicketAlt, label: 'Tickets', color: 'text-pink-500' },
  app_url: { icon: faServer, label: 'App URL', color: 'text-green-600' },
  key_files: { icon: faFolder, label: 'Key Files', color: 'text-gray-500' },
  top_level: { icon: faFolder, label: 'Project Structure', color: 'text-gray-600' },
};

const OverviewTab: React.FC<Props> = ({ validationId }) => {
  const [context, setContext] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchContext = async () => {
    try {
      setLoading(true);
      setError(null);
      const { data } = await agentsApi.getWorkspaceContext(validationId);
      if (data?.error) {
        setError(data.content?.[0]?.text || 'Failed to load context');
        setContext(null);
      } else if (data?.content?.[0]?.text) {
        setContext({ raw: data.content[0].text });
      } else {
        setContext(data);
      }
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Failed to load workspace context');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchContext();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [validationId]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <FontAwesomeIcon icon={faSync} className="animate-spin text-indigo-500 text-xl mr-3" />
        <span className="text-gray-500">Loading workspace context...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-4">
        <div className="flex justify-end">
          <button onClick={fetchContext} className="text-sm text-indigo-600 hover:text-indigo-800 flex items-center gap-1">
            <FontAwesomeIcon icon={faSync} />
            Retry
          </button>
        </div>
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
          <FontAwesomeIcon icon={faExclamationTriangle} className="text-red-400 mt-0.5" />
          <p className="text-sm text-red-700">{error}</p>
        </div>
      </div>
    );
  }

  if (!context) {
    return <div className="text-gray-400 py-8 text-center">No workspace context available</div>;
  }

  if (context.raw) {
    const sections = parseContextSections(context.raw);

    // Extract header info (first line: WORKSPACE CONTEXT title + type/status)
    const headerText = sections.header || '';
    const headerLines = headerText.split('\n').filter(Boolean);
    const titleLine = headerLines[0] || '';
    const metaLine = headerLines[1] || '';

    // Parse meta
    const metaParts = metaLine.split('|').map((s: string) => s.trim()).filter(Boolean);

    return (
      <div className="space-y-4">
        <div className="flex justify-end">
          <button onClick={fetchContext} className="text-sm text-indigo-600 hover:text-indigo-800 flex items-center gap-1">
            <FontAwesomeIcon icon={faSync} />
            Refresh
          </button>
        </div>

        {/* Header card */}
        {(titleLine || metaParts.length > 0) && (
          <div className="bg-gradient-to-r from-indigo-50 to-blue-50 rounded-lg border border-indigo-100 p-4">
            {titleLine && (
              <h2 className="text-lg font-semibold text-gray-900 mb-2">{titleLine.replace(/^"(.*)"$/, '$1')}</h2>
            )}
            {metaParts.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {metaParts.map((part: string, i: number) => {
                  const [key, val] = part.split(':').map((s: string) => s.trim());
                  return (
                    <span key={i} className="inline-flex items-center gap-1 px-2.5 py-1 bg-white rounded-full text-xs font-medium text-gray-700 border border-gray-200">
                      <span className="text-gray-400">{key}:</span> {val}
                    </span>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* Inline stats */}
        {(sections.tech_stack || sections.package_manager || sections.files) && (
          <div className="grid grid-cols-3 gap-3">
            {sections.tech_stack && (
              <div className="bg-white rounded-lg border border-gray-200 p-3">
                <div className="text-xs text-gray-400 uppercase tracking-wide mb-1">Tech Stack</div>
                <div className="flex flex-wrap gap-1">
                  {sections.tech_stack.split(',').map((t: string, i: number) => (
                    <span key={i} className="px-2 py-0.5 bg-blue-50 text-blue-700 rounded text-xs font-medium">{t.trim()}</span>
                  ))}
                </div>
              </div>
            )}
            {sections.package_manager && (
              <div className="bg-white rounded-lg border border-gray-200 p-3">
                <div className="text-xs text-gray-400 uppercase tracking-wide mb-1">Package Manager</div>
                <div className="text-sm font-medium text-gray-900">{sections.package_manager}</div>
              </div>
            )}
            {sections.files && (
              <div className="bg-white rounded-lg border border-gray-200 p-3">
                <div className="text-xs text-gray-400 uppercase tracking-wide mb-1">Files</div>
                <div className="text-sm font-medium text-gray-900">{sections.files}</div>
              </div>
            )}
          </div>
        )}

        {/* Content sections */}
        {['description', 'understanding', 'top_level', 'key_files', 'container', 'app_url', 'recent_agent_work', 'recent_activity', 'tickets'].map((key) => {
          const content = sections[key];
          if (!content) return null;

          const cfg = SECTION_CONFIG[key] || { icon: faFolder, label: key.replace(/_/g, ' '), color: 'text-gray-500' };

          return (
            <div key={key} className="bg-white rounded-lg border border-gray-200 overflow-hidden">
              <div className="px-4 py-3 border-b border-gray-100 flex items-center gap-2">
                <FontAwesomeIcon icon={cfg.icon} className={cfg.color} />
                <h3 className="font-medium text-gray-900 text-sm">{cfg.label}</h3>
              </div>
              <div className="p-4">
                {key === 'description' || key === 'understanding' ? (
                  <MarkdownRenderer content={content} className="text-sm" />
                ) : key === 'top_level' || key === 'key_files' ? (
                  <div className="font-mono text-xs text-gray-600 space-y-0.5">
                    {content.split('\n').filter(Boolean).map((line: string, i: number) => (
                      <div key={i} className="px-2 py-0.5 hover:bg-gray-50 rounded">{line.trim()}</div>
                    ))}
                  </div>
                ) : key === 'recent_agent_work' || key === 'recent_activity' ? (
                  <div className="space-y-1.5">
                    {content.split('\n').filter(Boolean).map((line: string, i: number) => (
                      <div key={i} className="text-sm text-gray-600 flex items-start gap-2">
                        <span className="text-gray-300 mt-0.5">&#8226;</span>
                        <span>{line.replace(/^\s*\[/, '[')}</span>
                      </div>
                    ))}
                  </div>
                ) : key === 'tickets' ? (
                  <div className="space-y-1.5">
                    {content.split('\n').filter(Boolean).map((line: string, i: number) => (
                      <div key={i} className="text-sm text-gray-700 font-mono px-2 py-1 bg-gray-50 rounded">{line.trim()}</div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-gray-700">{content}</p>
                )}
              </div>
            </div>
          );
        })}
      </div>
    );
  }

  return (
    <div className="text-gray-400 py-8 text-center">No workspace context available</div>
  );
};

export default OverviewTab;
